"use client";

import { useState } from "react";
import { identity, waHref } from "@/content/identity";

const contacts = [
  { name: "Jorge", phone: identity.telefonos[0], label: "Codiaj" },
  { name: "Jesús", phone: identity.telefonos[1], label: "Codiaj" },
].filter((contact) => Boolean(contact.phone));

const message = "¡Hola! Os escribo desde la web de Codiaj.";

function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 32 32"
      fill="currentColor"
      className={className}
    >
      <path d="M16.01 3.2c-7.04 0-12.76 5.72-12.76 12.76 0 2.25.59 4.44 1.71 6.37L3.14 29l6.84-1.79a12.7 12.7 0 0 0 6.03 1.53c7.04 0 12.76-5.72 12.76-12.76S23.05 3.2 16.01 3.2Zm0 23.37c-1.92 0-3.79-.52-5.43-1.51l-.39-.23-4.06 1.06 1.08-3.96-.26-.41a10.54 10.54 0 0 1-1.53-5.55c0-5.85 4.75-10.6 10.6-10.6s10.6 4.75 10.6 10.6-4.76 10.6-10.61 10.6Zm5.82-7.93c-.32-.16-1.9-.94-2.2-1.05-.29-.11-.5-.16-.72.16-.21.32-.83 1.05-1.02 1.26-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.58-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.15-.15.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55h-.61c-.21 0-.56.08-.85.4-.29.32-1.12 1.1-1.12 2.68s1.15 3.1 1.31 3.31c.16.21 2.26 3.45 5.47 4.84.76.33 1.36.53 1.83.68.77.24 1.47.21 2.02.13.62-.09 1.9-.78 2.17-1.53.27-.75.27-1.39.19-1.53-.08-.13-.29-.21-.61-.37Z" />
    </svg>
  );
}

export function WhatsAppWidget() {
  const [isOpen, setIsOpen] = useState(false);

  if (contacts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      <div
        className={`flex flex-col items-end gap-2 transition-all duration-300 origin-bottom-right ${
          isOpen
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-3 scale-95 opacity-0"
        }`}
      >
        <div className="max-w-[230px] rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)]/95 px-4 py-3 text-right shadow-[0_18px_50px_rgba(0,0,0,0.28)] backdrop-blur-md">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--color-mute)]">
            ¿Hablamos por WhatsApp?
          </p>
          <p className="mt-1 mb-0 font-sans text-[13px] leading-[1.45] text-[color:var(--color-ink)]">
            Elige con quién quieres contactar.
          </p>
        </div>

        {contacts.map((contact) => (
          <a
            key={contact.name}
            href={`${waHref(contact.phone)}?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex min-w-[190px] items-center justify-between gap-4 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-4 py-3 no-underline shadow-[0_14px_40px_rgba(0,0,0,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#25D366]/60 active:scale-95"
          >
            <span className="flex flex-col">
              <span className="font-sans text-[14px] font-semibold text-[color:var(--color-ink)]">
                {contact.name}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[color:var(--color-mute)] transition-colors group-hover:text-[#25D366]">
                {contact.label}
              </span>
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_22px_rgba(37,211,102,0.35)]">
              <WhatsAppIcon className="h-5 w-5" />
            </span>
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={isOpen ? "Cerrar opciones de WhatsApp" : "Abrir opciones de WhatsApp"}
        className={`relative flex h-14 w-14 items-center justify-center rounded-full shadow-[0_14px_36px_rgba(0,0,0,0.34)] transition-all duration-300 active:scale-95 ${
          isOpen
            ? "rotate-90 bg-[color:var(--color-ink)] text-[color:var(--color-bg)]"
            : "bg-[#25D366] text-white hover:scale-105 hover:bg-[#1EBE5D]"
        }`}
      >
        {isOpen ? (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="h-6 w-6"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        ) : (
          <WhatsAppIcon className="h-8 w-8" />
        )}

        {!isOpen && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[color:var(--color-ink)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#25D366]" />
          </span>
        )}
      </button>
    </div>
  );
}
